import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
  COLOR_BACKGROUND_WARM,
  COLOR_BACKGROUND_LIGHT,
  COLOR_MIDDLE_GRAY,
  COLOR_WHITE,
  COLOR_BLUE,
  COLOR_GREEN,
  COLOR_BORDER,
} from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import Accordion from '../atoms/Accordion';
import Ranks, { RanksType } from '../atoms/Ranks';
import Player from '../types/Player';
import Team from '../types/Team';
import Position from '../types/Position';
import get from 'lodash/get';
import { getEventName } from '../services/event-names-and-formats';
import { RanksComparisonGroup } from './RanksTooltip';
import { UpdatedPlayerPPIStat, UpdatedPlayerPPIStats as UpdatedPlayerPPIStatsType, YearlyPPIStats } from '../types/PlayerPPIStats';
import Column from '../types/Column';
import { DEFAULT_PROFILE_STATS_COLUMNS, getCollegeHeaderItemByColumn, getNflHeaderItemByColumn, getProfileStatsComparisonRankValue } from '../services/profile-stats-categories';
import HighSchool from '../types/HighSchool';
import College from '../types/College';
import { StatsItem } from '../types/StatsPlayer';
import { State } from '../redux/reducers';
import InfoIcon from '../icons/InfoIcon';
import Tooltip from '../atoms/Tooltip';
import {
  BLOCKING_PPI_TOOLTIPS,
  DEFENSIVE_PPI_TOOLTIPS,
  PASSING_PPI_TOOLTIPS,
  RECEIVING_PPI_TOOLTIPS,
  RUSHING_PPI_TOOLTIPS,
} from '../services/tooltips';
import LabledScore, { SCORE_TYPE } from '../atoms/LabledScore';
import { useLocation } from 'react-router-dom';
interface PPIChartsDataProps {
    className?: string;
    player?: Player;
    collegeTeam?: Team;
    nflTeam?: Team;
    position?: Position;
    onYearClick?: (year: number) => void;
    isNFL?: boolean;
    columns?: Column[];
    ppiStats?: UpdatedPlayerPPIStat;
    comparisonEvent?: string;
}

interface YearPPIView {
  year: number;
  ppis: UpdatedPlayerPPIStatsType[];
}

enum PPI_COMPARISON_TYPES {
    OVERALL_PPI = 'overall-ppi',
    PASSING_PPI = 'passing-ppi',
    RUSHING_PPI = 'rushing-ppi',
    RECEIVING_PPI = 'receiving-ppi',
    BLOCKING_PPI = 'blocking-ppi',
    DEFENSIVE_PPI = 'defensive-ppi',
  }

  enum PPI_TYPES {
    OVERALL_PPI = 'Overall PPI',
    PASSING_PPI = 'Passing PPI',
    RUSHING_PPI = 'Rushing PPI',
    RECEIVING_PPI = 'Receiving PPI',
    BLOCKING_PPI = 'Blocking PPI',
    DEFENSIVE_PPI = 'Defensive PPI',
  }

function normalizeStatsItem(stats?: StatsItem | null) {
  return {
    percentile: Number(stats?.percentile) || 0,
    playerRank: Number(stats?.playerRank) || 0,
    playerCount: Number(stats?.playerCount) || 0,
  };
}

function toComparisonStats(stats: YearlyPPIStats['overallPPI']) {
  if (!stats || !Array.isArray(stats.comparisonStats)) {
    return [];
  }

  return stats.comparisonStats.map((item: StatsItem | null | undefined) => normalizeStatsItem(item));
}

function getUtilizationType(utilization: number, PPIType: string): string {
  switch (PPIType) {
    case PPI_TYPES.OVERALL_PPI:
      if (utilization >= 100) return 'High';
      if (utilization >= 60) return 'Medium';
      return 'Low';
      break;
    case PPI_TYPES.PASSING_PPI:
      if (utilization >= 100) return 'High';
      if (utilization >= 60) return 'Medium';
      return 'Low';
      break;
    case PPI_TYPES.RUSHING_PPI:
      if (utilization >= 150) return 'High';
      if (utilization >= 80) return 'Medium';
      return 'Low';
      break;
    case PPI_TYPES.RECEIVING_PPI:
      if (utilization >= 150) return 'High';
      if (utilization >= 80) return 'Medium';
      return 'Low';
      break;
    case PPI_TYPES.BLOCKING_PPI:
      if (utilization >= 500) return 'High';
      if (utilization >= 200) return 'Medium';
      return 'Low';
      break;
    case PPI_TYPES.DEFENSIVE_PPI:
      if (utilization >= 500) return 'High';
      if (utilization >= 200) return 'Medium';
      return 'Low';
      break;
    default:
      return 'Low';

  }
}
function getVersatilityType(versatilityIndex: number): string {
  if (versatilityIndex >= 2.8) return 'High';
  if (versatilityIndex >= 1.4) return 'Medium';
  return 'Low';
}

const STOP_RATE_LOW = 6;
const STOP_RATE_HIGH = 10;
const MIN_STOP_RATE_PLAYS = 40;
// Critical Tackle Rates
const TACKLE_RATE_HIGH = 15;
const TACKLE_RATE_LOW = 5;
// Critical CPOE Rates
const CPOE_RATE_HIGH = 10;
const CPOE_RATE_LOW = -10;
const MIN_CPOE_RATE_PLAYS = 1;
// Critical Passing Rating Against
const PASSER_RATING_AGAINST_HIGH = 100;
const PASSER_RATING_AGAINST_LOW = 75;
const MIN_PASSER_RATING_AGAINST_PLAYS = 1;




function getMetricIcon({
  value,
  lowThreshold,
  highThreshold,
  plays,
  minimumPlays,
  isReverseIcon = false
}: {
  value?: number | null;
  lowThreshold?: number;
  highThreshold?: number;
  plays?: number | null;
  minimumPlays?: number;
  isReverseIcon?: boolean;
}): string {
  const metricValue = Number(value);

  if (!Number.isFinite(metricValue)) {
    return '';
  }

  if (typeof minimumPlays === 'number') {
    const playCount = Number(plays);
    if (!Number.isFinite(playCount) || playCount < minimumPlays) {
      return '';
    }
  }

  if (isReverseIcon) {
    if (typeof highThreshold === 'number' && metricValue >= highThreshold) return '❌';
    if (typeof lowThreshold === 'number' && metricValue <= lowThreshold) return '✅';
  }

  if (typeof highThreshold === 'number' && metricValue >= highThreshold) return '✅';
  if (typeof lowThreshold === 'number' && metricValue <= lowThreshold) return '❌';

  return '↔️';
}

function getOverallStopRateLine(yearStat: YearlyPPIStats): string {
  const icon = getMetricIcon({
    value: yearStat.ctOverallStopRate,
    lowThreshold: STOP_RATE_LOW,
    highThreshold: STOP_RATE_HIGH,
    plays: yearStat.plays,
    minimumPlays: MIN_STOP_RATE_PLAYS,
  });
  return icon ? `${icon} ${yearStat.ctOverallStopRate}% Overall Stop Rate ℹ️` : '';
}

function toPPIItems(yearStat: YearlyPPIStats): UpdatedPlayerPPIStatsType[] {
  const items: UpdatedPlayerPPIStatsType[] = [];
  if (yearStat.overallPPI) {
  const comparisonStats = toComparisonStats(yearStat.overallPPI);
  items.push({
    name: PPI_TYPES.OVERALL_PPI,
    value: Number(yearStat.overallPPI.ppi) || 0,
    comparisonStats,
    factors: [],
    percentile: Number(yearStat.overallPercentile) || 0,
    utilization: 0,
    utilizationType: getUtilizationType(Number(yearStat.rushingUtilization) || 0, PPI_TYPES.OVERALL_PPI),
    totalCount: yearStat.overallTotalCount,
    rank: yearStat.overallRank,
    position: yearStat.position,
  });
  }

  if (yearStat.passingPPI) {
  const comparisonStats = toComparisonStats(yearStat.passingPPI);
  const passingUtilization = Number(yearStat.passingUtilization) || 0;
  items.push({
    name: PPI_TYPES.PASSING_PPI,
    value: Number(yearStat.passingPPI.ppi) || 0,
    comparisonStats,
    factors: [
      { name: 'Efficiency', value: Number(yearStat.passingEfficiency) || 0, total: 5, tooltip: PASSING_PPI_TOOLTIPS.EFFICIENCY },
      { name: 'Touchdowns', value: Number(yearStat.passingTouchdowns) || 0, total: 5, tooltip: PASSING_PPI_TOOLTIPS.TOUCHDOWNS },
      { name: 'Explosiveness', value: Number(yearStat.passingExplosiveness) || 0, total: 5, tooltip: PASSING_PPI_TOOLTIPS.EXPLOSIVENESS },
      { name: 'INTs', value: Number(yearStat.passingInt) || 0, total: 5, tooltip: PASSING_PPI_TOOLTIPS.INTS },
    ],
    percentile: Number(yearStat.passingPercentile) || 0,
    utilization: passingUtilization,
    utilizationType: getUtilizationType(passingUtilization, PPI_TYPES.PASSING_PPI),
    teamFactors: [
      `${yearStat.tfOffensiveHardestDefense ? `Played the #${yearStat.tfOffensiveHardestDefense} hardest offenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfOffensiveBestOffense ? `Played on the #${yearStat.tfOffensiveBestOffense} best defenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `Played the #${yearStat.pfPassingHardestDefense} hardest offenses (out of ${yearStat.pfPassingTotalPlayers} players).`,
      `Played on the #${yearStat.pfPassingBestOffense} best defense (out of ${yearStat.pfPassingTotalPlayers} players).`
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.passingTotalCount,
    rank: yearStat.passingRank,
    position: yearStat.position
  });
  }

  if (yearStat.rushingPPI) {
  const comparisonStats = toComparisonStats(yearStat.rushingPPI);
  const rushingUtilization = Number(yearStat.rushingUtilization) || 0;
  items.push({
    name: PPI_TYPES.RUSHING_PPI,
    value: Number(yearStat.rushingPPI.ppi) || 0,
    comparisonStats,
    factors: [
    { name: 'Power', value: Number(yearStat.rushingPower) || 0, total: 5, tooltip: RUSHING_PPI_TOOLTIPS.POWER },
    { name: 'Efficiency', value: Number(yearStat.rushingEfficiency) || 0, total: 5, tooltip: RUSHING_PPI_TOOLTIPS.EFFICIENCY },
    { name: 'Explosiveness', value: Number(yearStat.rushingExplosiveness) || 0, total: 5, tooltip: RUSHING_PPI_TOOLTIPS.EXPLOSIVENESS },
    ],
    percentile: Number(yearStat.rushingPercentile) || 0,
    utilization: rushingUtilization,
    utilizationType: getUtilizationType(rushingUtilization, PPI_TYPES.RUSHING_PPI),
    teamFactors: [
      `${yearStat.tfOffensiveHardestDefense ? `Played the #${yearStat.tfOffensiveHardestDefense} hardest offenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfOffensiveBestOffense ? `Played on the #${yearStat.tfOffensiveBestOffense} best defenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `${yearStat.pfRushingHardestDefense ? `Played the #${yearStat.pfRushingHardestDefense} hardest offenses (out of ${yearStat.pfRushingTotalPlayers} players).` : ''}`,
      `${yearStat.pfRushingBestOffense ? `Played on the #${yearStat.pfRushingBestOffense} best defense (out of ${yearStat.pfRushingTotalPlayers} players).` : ''}`
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.rushingTotalCount,
    rank: yearStat.rushingRank,
    position: yearStat.position
  });
  }

  if (yearStat.receivingPPI) {
  const comparisonStats = toComparisonStats(yearStat.receivingPPI);
  const receivingUtilization = Number(yearStat.receivingUtilization) || 0;
  items.push({
    name: PPI_TYPES.RECEIVING_PPI,
    value: Number(yearStat.receivingPPI.ppi) || 0,
    comparisonStats,
    factors: [
    { name: 'Reliability', value: Number(yearStat.receivingReliability) || 0, total: 5, tooltip: RECEIVING_PPI_TOOLTIPS.RELIABILITY },
    { name: 'Impactfulness', value: Number(yearStat.receivingImpactfulness) || 0, total: 5, tooltip: RECEIVING_PPI_TOOLTIPS.IMPACTFULNESS },
    { name: 'Explosiveness', value: Number(yearStat.receivingExplosiveness) || 0, total: 5, tooltip: RECEIVING_PPI_TOOLTIPS.EXPLOSIVENESS },
    ],
    percentile: Number(yearStat.receivingPercentile) || 0,
    utilization: receivingUtilization,
    utilizationType: getUtilizationType(receivingUtilization, PPI_TYPES.RECEIVING_PPI),
    teamFactors: [
      `${yearStat.tfOffensiveHardestDefense ? `Played the #${yearStat.tfOffensiveHardestDefense} hardest offenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfOffensiveBestOffense ? `Played on the #${yearStat.tfOffensiveBestOffense} best defenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `${yearStat.pfReceivingHardestDefense ? `Played the #${yearStat.pfReceivingHardestDefense} hardest offenses (out of ${yearStat.pfReceivingTotalPlayers} players).` : ''}`,
      `${yearStat.pfReceivingBestOffense ? `Played on the #${yearStat.pfReceivingBestOffense} best defense (out of ${yearStat.pfReceivingTotalPlayers} players).` : ''}`
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.receivingTotalCount,
    rank: yearStat.receivingRank,
    position: yearStat.position
  });
  }

  if (yearStat.blockPPI) {
  const comparisonStats = toComparisonStats(yearStat.blockPPI);
  items.push({
    name: PPI_TYPES.BLOCKING_PPI,
    value: Number(yearStat.blockPPI.ppi) || 0,
    comparisonStats,
    factors: [
      { name: 'Run Blocking', value: Number(yearStat.runBlockPpi) || 0, total: 5, tooltip: BLOCKING_PPI_TOOLTIPS.RUN_BLOCKING },
      { name: 'Pass Blocking', value: Number(yearStat.passBlockPpi) || 0, total: 5, tooltip: BLOCKING_PPI_TOOLTIPS.PASS_BLOCKING },
    ],
    percentile: Number(yearStat.blockingPercentile) || 0,
    utilization: yearStat.blockingSnaps,
    utilizationType: getUtilizationType(yearStat.blockingSnaps, PPI_TYPES.BLOCKING_PPI),
    teamFactors: [
      `${yearStat.tfOffensiveHardestDefense ? `Played the #${yearStat.tfOffensiveHardestDefense} hardest offenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfOffensiveBestOffense ? `Played the #${yearStat.tfOffensiveBestOffense} best defenses (out of ${yearStat.tfOffensiveTotalTeams} teams).` : ''}`,
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `${yearStat.pfBlockingHardestDefense ? `Played the #${yearStat.pfDefensiveHardestOffense} hardest offenses (out of ${yearStat.pfDefensiveTotalPlayers} players).` : ''}`,
      `${yearStat.pfBlockingHardestDefense ? `Played the #${yearStat.pfBlockingHardestDefense} hardest offenses (out of ${yearStat.pfBlockingTotalPlayers} players).` : ''}`,
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.blockingTotalCount,
    rank: yearStat.blockingRank,
    position: yearStat.position
  });
  }

  if (yearStat.defensePPI) {
  const comparisonStats = toComparisonStats(yearStat.defensePPI);
  items.push({
    name: PPI_TYPES.DEFENSIVE_PPI,
    value: Number(yearStat.defensePPI.ppi) || 0,
    comparisonStats,
    factors: [
      { name: 'Run Defense', value: Number(yearStat.runDefensePpi) || 0, total: 5, tooltip: DEFENSIVE_PPI_TOOLTIPS.RUN_DEFENSE },
      { name: 'Pass Coverage', value: Number(yearStat.passCoveragePpi) || 0, total: 5, tooltip: DEFENSIVE_PPI_TOOLTIPS.PASS_COVERAGE },
      { name: 'Pass Rush', value: Number(yearStat.passRushPpi) || 0, total: 5, tooltip: DEFENSIVE_PPI_TOOLTIPS.PASS_RUSH },
    ],
    percentile: Number(yearStat.defensePercentile) || 0,
    utilization: yearStat.plays,
    utilizationType: getUtilizationType(yearStat.plays, PPI_TYPES.DEFENSIVE_PPI),
    teamFactors: [
      `${yearStat.tfDefensiveHardestOffense ? `Played the #${yearStat.tfDefensiveHardestOffense} hardest offenses (out of ${yearStat.tfDefensiveTotalTeams} teams).` : ''}`,
      `${yearStat.tfDefensiveBestDefense ? `Played on the #${yearStat.tfDefensiveBestDefense} best defenses (out of ${yearStat.tfDefensiveTotalTeams} teams).` : ''}`
    ].filter(line => line.trim() !== ''),
    playerFactors: [
      `${yearStat.pfDefensiveHardestOffense ? `Played the #${yearStat.pfDefensiveHardestOffense} hardest offenses (out of ${yearStat.pfDefensiveTotalPlayers} players).` : ''}`,
      `${yearStat.pfDefensiveBestDefense ? `Played on the #${yearStat.pfDefensiveBestDefense} best defenses (out of ${yearStat.pfDefensiveTotalPlayers} players).` : ''}`
    ].filter(line => line.trim() !== ''),
    totalCount: yearStat.defenseTotalCount,
    rank: yearStat.defenseRank,
    position: yearStat.position,
    versatilityIndex: yearStat.versatilityIndex,
    criticalFactors:[
      `${ yearStat.ctPasserRatingAgainst ? `${getMetricIcon({
         value: yearStat.ctPasserRatingAgainst,
         lowThreshold: PASSER_RATING_AGAINST_LOW,
          highThreshold: PASSER_RATING_AGAINST_HIGH,
          plays: yearStat.plays,
          minimumPlays: MIN_PASSER_RATING_AGAINST_PLAYS,
          isReverseIcon: true,
         })} ${yearStat.ctPasserRatingAgainst} Passer Rating Against – ${yearStat.ctCompletionsAgainst} for ${yearStat.ctAttemptsAgainst} (${yearStat.ctCompletionPercentageAgainst}%), ${yearStat.ctPassYardsAgainst} yds, ${yearStat.ctTouchdownsAgainst} TDs, ${yearStat.ctInterceptions} INTs` : ''}`,
      `${ yearStat.ctCpoe ? `${getMetricIcon({
        value: yearStat.ctCpoe,
        lowThreshold: CPOE_RATE_LOW,
        highThreshold: CPOE_RATE_HIGH,
        plays: yearStat.plays,
        minimumPlays: MIN_CPOE_RATE_PLAYS,
        isReverseIcon: true,
       })} ${yearStat.ctCpoe > 0 ? '+' : ''}${yearStat.ctCpoe}% CPOE Against ℹ️` : ''}`,
      `${ yearStat.ctOverallTackleRate ? `${getMetricIcon({ 
        value: yearStat.ctOverallTackleRate,
        lowThreshold: TACKLE_RATE_LOW,
        highThreshold: TACKLE_RATE_HIGH,
        plays: yearStat.plays,
        minimumPlays: MIN_STOP_RATE_PLAYS,
       })} ${yearStat.ctOverallTackleRate}% Overall Tackle Rate ℹ️` : ''}`,
      `${getOverallStopRateLine(yearStat)}`,
      `${ yearStat.ctPrimaryCoveragePlays ? `⚠️ Limited passes in primary coverage (${yearStat.ctPrimaryCoveragePlays}).` : '⚠️ Limited passes in primary coverage'}`,
      `${ yearStat.ctPassRushPlays ? `⚠️ Limited pass rush opportunities (${yearStat.ctPassRushPlays}).` : '⚠️ Limited pass rush opportunities'}`,
    ].filter(line => line.trim() !== ''),
    
    defenseOverallPpi: yearStat.defenseOverallPpi,
    defenseOverallSafetyPpi: yearStat.defenseOverallSafetyPpi,
    defenseOverallCornerPpi: yearStat.defenseOverallCornerPpi,
    defenseOverallOlbPpi: yearStat.defenseOverallOlbPpi,
    defenseOverallMlbPpi: yearStat.defenseOverallMlbPpi,
    defenseOverallOlbEdgePpi: yearStat.defenseOverallOlbEdgePpi,
    defenseOverallIlPpi: yearStat.defenseOverallIlPpi,
    runDefensePpi: yearStat.runDefensePpi,
    runDefenseSafetyPpi: yearStat.runDefenseSafetyPpi,
    runDefenseCornerPpi: yearStat.runDefenseCornerPpi,
    runDefenseOlbPpi: yearStat.runDefenseOlbPpi,
    runDefenseMlbPpi: yearStat.runDefenseMlbPpi,
    runDefenseOlbEdgePpi: yearStat.runDefenseOlbEdgePpi,
    runDefenseIlPpi: yearStat.runDefenseIlPpi,
    passRushPpi: yearStat.passRushPpi,
    passRushSafetyPpi: yearStat.passRushSafetyPpi,
    passRushCornerPpi: yearStat.passRushCornerPpi,
    passRushOlbPpi: yearStat.passRushOlbPpi,
    passRushMlbPpi: yearStat.passRushMlbPpi,
    passRushOlbEdgePpi: yearStat.passRushOlbEdgePpi,
    passRushIlPpi: yearStat.passRushIlPpi,
    passCoveragePpi: yearStat.passCoveragePpi,
    passCoverageSafetyPpi: yearStat.passCoverageSafetyPpi,
    passCoverageCornerPpi: yearStat.passCoverageCornerPpi,
    passCoverageOlbPpi: yearStat.passCoverageOlbPpi,
    passCoverageMlbPpi: yearStat.passCoverageMlbPpi,
    passCoverageOlbEdgePpi: yearStat.passCoverageOlbEdgePpi,
    passCoverageIlPpi: yearStat.passCoverageIlPpi,
    plays: yearStat.plays,
    pctPlaySafety: yearStat.pctPlaySafety,
    pctPlayCorner: yearStat.pctPlayCorner,
    pctPlayOlb: yearStat.pctPlayOlb,
    pctPlayMlb: yearStat.pctPlayMlb,
    pctPlayOlbEdge: yearStat.pctPlayOlbEdge,
    pctPlayIl: yearStat.pctPlayIl,
  });
  }

  return items;
}

const useStyles = makeStyles((theme: Theme) => ({
  addRemoveColumns: {
    marginBottom: theme.spacing(2),
  },
  yearsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    overflowX: 'auto',
    padding: theme.spacing(2),
    WebkitOverflowScrolling: 'touch',
  },
  yearButton: {
    position: 'relative',
    display: 'inline-flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 1, 2),
    minWidth: 136,
    minHeight: 56,
    borderRadius: 999,
    border: `2px solid ${COLOR_MIDDLE_GRAY}`,
    backgroundColor: COLOR_BACKGROUND_WARM,
    cursor: 'pointer',
    outline: 'none',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    whiteSpace: 'nowrap',
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(14),

    '&:hover': {
      backgroundColor: COLOR_MIDDLE_GRAY,
    },
  },
  yearButtonSelected: {
    borderColor: COLOR_BLUE,
    backgroundColor: '#E7F4FF',
  },
  yearLabel: {
    fontWeight: 600,
  },
  scorePill: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 999,
    backgroundColor: '#0f89e6',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(14),
    minWidth: 60,
    minHeight: 36,
    textAlign: 'center',
  },
  ppiWrapper: {
  },
  ppiTopRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4, 3),
    
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(3, 2),
    },
  },
  ppiTopRowCentered: {
    justifyContent: 'center',
  },
  ppiBars: {
    flexGrow: 1,
    maxWidth: 400,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    
    '@media (max-width: 580px)': {
      gap: theme.spacing(2),
    },
  },
  ppiBarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(.25, 1.5),
    width: '100%',
    fontWeight: 700,
    
    '@media (max-width: 580px)': {
      flexWrap: 'wrap',
    },
  },
  ppiBarTrack: {
    flexShrink: 0,
    width: 200,
    height: 8,
    borderRadius: 999,
    backgroundColor: '#e5e5e5',
    overflow: 'hidden',
    
    '@media (max-width: 580px)': {
      flexGrow: 1,
      width: 140,
    },
  },
  ppiBarFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: '#5eba6d',
    transition: 'width 0.3s ease',
  },
  ppiBarMeta: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.1,
    
    '@media (max-width: 580px)': {
      width: '100%',
    },
  },
  ppiBarLabels: {
    marginRight: 'auto',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    
    '@media (max-width: 580px)': {
      marginRight: 0,
    },
  },
  infoIcon: {
    flexShrink: 0,
    width: 32,
    height: 32,
  },
  ppiSummaryRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    padding: theme.spacing(0, 3),
    
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(0, 2),
    },
    
    '@media (max-width: 1260px)': {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  ppiSummaryCard: {
    flex: '1 1 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(.5),
    minWidth: 0,
    padding: theme.spacing(1.5, 2),
    textAlign: 'center',
    border: `1px solid ${COLOR_BACKGROUND_WARM}`,
    backgroundColor: COLOR_BACKGROUND_LIGHT,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1.1,
    
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  ppiSummaryTitle: {
    fontWeight: 700,
  },
  ppiSummaryValue: {
    fontSize: theme.typography.pxToRem(24),
    lineHeight: 1,
    fontWeight: 700,
  },
  ppiSummaryValueChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 1.25),
    borderRadius: 999,
    border: '2px solid transparent',
    fontSize: theme.typography.pxToRem(18),
    lineHeight: 1,
  },
  ppiSummaryValueChipHigh: {
    backgroundColor: '#E2FFE7',
    borderColor: COLOR_GREEN,
  },
  ppiSummaryValueChipMedium: {
    backgroundColor: '#FFF6DB',
    borderColor: '#F5D460',
  },
  ppiSummaryValueChipLow: {
    backgroundColor: COLOR_WHITE,
    borderColor: '#979797',
  },
  ppiSummarySub: {
  },
  ppiSummaryInfoCenter: {
    display: 'flex',
    justifyContent: 'center',
  },
  ppiTableContainer: {
    overflow: 'auto',
  },
  ppiTable: {
    minWidth: 500,
    width: '100%',
    borderCollapse: 'collapse' as const,
    borderBottom: `1px solid ${COLOR_BORDER}`,
    lineHeight: 1.1,
    
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },
  ppiTableHeadCell: {
    padding: theme.spacing(1),
    border: `1px solid ${COLOR_BORDER}`,
    fontWeight: 700,

    '&:first-child': {
      borderLeft: 0,
    },

    '&:last-child': {
      borderRight: 0,
    },
  },
  ppiTableCell: {
    padding: theme.spacing(1.5, 1),
    borderRight: `1px solid ${COLOR_BORDER}`,
    textAlign: 'center',
    fontWeight: 600,
    backgroundColor: COLOR_WHITE,

    '&:last-child': {
      borderRight: 0,
    },

    'tbody tr:nth-child(odd) &': {
      backgroundColor: COLOR_BACKGROUND_LIGHT,
    },
  },
  ppiTableHighlightCell: {
    backgroundColor: '#E2FFE7',

    'tbody tr:nth-child(odd) &': {
      backgroundColor: '#E2FFE7',
    },
  },
  ppiTextSections: {
    marginTop: theme.spacing(5),
    padding: theme.spacing(0, 5, 5),
    
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(3),
      padding: theme.spacing(0, 3, 3),
    },
    
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(0, 2, 2),
      fontSize: theme.typography.pxToRem(14),
    },
  },
  ppiTextSection: {
    marginTop: theme.spacing(3),

    '&:first-child': {
      marginTop: 0,
    },
  },
  ppiTextTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  ppiBullet: {
  },
}), { name: PPIChartsData.name });

export default function PPIChartsData(props: PPIChartsDataProps) {
  const { className, player, collegeTeam, nflTeam, onYearClick, isNFL, ppiStats, columns, position, comparisonEvent } = props;
  const classes = useStyles();
  const location = useLocation();
  const newProfileEnabled = location.pathname.includes('player-profile/');
  const college = ((player && player.playerColleges) || [])
      .find((college:College) => college && college.isPrimary);
  const highSchool = ((player && player.highSchools) || [])
    .find((highSchool:HighSchool) => highSchool && highSchool.isPrimary);
  const graduationYear = highSchool?.graduatingClass || 0;
  const recruitingClass = graduationYear || college?.recruitingClass;

  const selectedColumns = (columns?.filter((column:Column) => column.selected).length
      ? columns.filter((column:Column) => column.selected)
      : DEFAULT_PROFILE_STATS_COLUMNS) as Column[];

  const yearlyData: YearPPIView[] = useMemo(
    () => (ppiStats?.yearlyStats || [])
      .map((yearStat) => ({
        year: Number(yearStat.year) || 0,
        ppis: toPPIItems(yearStat),
      }))
      .sort((a, b) => b.year - a.year),
    [ppiStats],
  );

  const yearScores = useMemo(
    () => yearlyData.map((entry) => ({
      year: entry.year,
      score: (Number(entry.ppis.find((ppi) => ppi.name === PPI_TYPES.OVERALL_PPI)?.value) || 0).toFixed(1),
    })),
    [yearlyData],
  );

  const [selectedYear, setSelectedYear] = useState<number>(yearScores[0]?.year || 0);

  useEffect(() => {
    if (yearScores.length > 0 && !yearScores.some((entry) => entry.year === selectedYear)) {
      setSelectedYear(yearScores[0].year);
    }
  }, [yearScores, selectedYear]);

  function handleYearClick(year: number) {
    setSelectedYear(year);
    if (onYearClick) onYearClick(year);
  }

  const selectedYearData = yearlyData.find((year) => year.year === selectedYear);
  const selectedYearPPIData = selectedYearData?.ppis || [];

function getComparisonEventByPPIType(ppiType: string) {
  switch (ppiType) {
    case PPI_TYPES.OVERALL_PPI:
      return PPI_COMPARISON_TYPES.OVERALL_PPI;
    case PPI_TYPES.PASSING_PPI:
      return PPI_COMPARISON_TYPES.PASSING_PPI;
    case PPI_TYPES.RUSHING_PPI:
      return PPI_COMPARISON_TYPES.RUSHING_PPI;
    case PPI_TYPES.RECEIVING_PPI:
      return PPI_COMPARISON_TYPES.RECEIVING_PPI;
    case PPI_TYPES.BLOCKING_PPI:
      return PPI_COMPARISON_TYPES.BLOCKING_PPI;
    case PPI_TYPES.DEFENSIVE_PPI:
      return PPI_COMPARISON_TYPES.DEFENSIVE_PPI;
    default:
      return '';
  }
}


  function renderPPIStats (index: number, name: string, stat: UpdatedPlayerPPIStatsType) {
    const updatedComparisonEvent = getComparisonEventByPPIType(name);
    return (
        <Accordion
        key={name}
        summary={`${selectedYearData?.year} ${name}`}
        expanded={index === 0}
      >
        <>
            {!isNFL && (
              <Ranks
                headerItems={selectedColumns.map((column:Column) => getCollegeHeaderItemByColumn(column, {
                  recruitingClass,
                  collegeTeam,
                }))}
              />
            )}

            {isNFL && (
              <Ranks
                headerItems={selectedColumns.map((column:Column) => getNflHeaderItemByColumn(column, {
                  collegeTeam,
                  nflTeam,
                }))}
              />
            )}
            <Ranks
                key={`${selectedYearData?.year} ${name}`}
                type={RanksType.PPI}
                playerId={player?.id}
                teamId={collegeTeam?.id}
                collegeTeamId={collegeTeam?.id}
                position={position}
                isNFL={isNFL}
                nflTeamId={nflTeam?.id}
                comparisonEvent={updatedComparisonEvent}
                values={selectedColumns.map((column:Column, columnIndex:number) => {
                  const comparisonStats = get(stat, 'comparisonStats', []);
                  return getProfileStatsComparisonRankValue({
                    column,
                    columnIndex,
                    comparisonStats,
                    isNFL,
                    newProfileEnabled,
                  });
                })}
                selectedPPIYear={selectedYear}
                noTopBorder
            />
            {/* PPI detail UI */}
            <div className={classes.ppiWrapper}>
              <div className={clsx(classes.ppiTopRow, !(stat.factors || []).length && classes.ppiTopRowCentered)}>
                {/* Score circle and factor bars */}
                <LabledScore
                  // className={classes.playerScore}
                  // noScoreTextClass={classes.noScoreText}
                  // checkIconClass={classes.checkIconClass}
                  type={SCORE_TYPE.PPI}
                  value={stat.value || undefined}
                  label='PPI'
                />

                {!!(stat.factors || []).length && (
                  <div className={classes.ppiBars}>
                    {(stat.factors || []).map((factor, idx) => {
                      const ratio = factor.total ? factor.value / factor.total : 0;
                      const width = `${Math.min(100, Math.max(0, ratio * 100))}%`;
                      return (
                        <div key={`${factor.name}-${idx}`} className={classes.ppiBarRow}>
                          <span>{factor.value.toFixed(1)}</span>
                          <div className={classes.ppiBarTrack}>
                            <div className={classes.ppiBarFill} style={{ width }} />
                          </div>
                          <div className={classes.ppiBarMeta}>
                          <div className={classes.ppiBarLabels}>{factor.name}</div>
                            <Tooltip title={factor.tooltip || ''} placement="top">
                              <InfoIcon className={classes.infoIcon} />
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Summary cards */}
              <div className={classes.ppiSummaryRow}>
                <div className={classes.ppiSummaryCard}>
                  <div className={classes.ppiSummaryTitle}>{selectedYearData?.year} Rank</div>
                  <div className={classes.ppiSummaryValue}>#{stat.rank ?? 0}</div>
                  <div className={classes.ppiSummarySub}>
                    out of {stat.totalCount ?? 0} {stat.position}s
                  </div>
                </div>
                <div className={classes.ppiSummaryCard}>
                  <div className={classes.ppiSummaryTitle}>Percentile</div>
                  <div className={classes.ppiSummaryValue}>
                    {Math.round(stat.percentile ?? 0)}%
                  </div>
                  <div className={classes.ppiSummarySub}>in FBS</div>
                </div>
                {
                  stat.utilization ?
                    <div className={classes.ppiSummaryCard}>
                      <div className={classes.ppiSummaryTitle}>Utilization</div>
                      <div
                        className={clsx(
                          classes.ppiSummaryValue,
                          classes.ppiSummaryValueChip,
                          (stat.utilizationType ?? 'Medium') === 'High' && classes.ppiSummaryValueChipHigh,
                          (stat.utilizationType ?? 'Medium') === 'Medium' && classes.ppiSummaryValueChipMedium,
                          (stat.utilizationType ?? 'Medium') === 'Low' && classes.ppiSummaryValueChipLow,
                        )}
                      >
                        {stat.utilizationType ?? 'Medium'}
                      </div>
                      <div className={classes.ppiSummarySub}>
                        {stat.utilization ?? 0} {(stat?.name == PPI_TYPES.DEFENSIVE_PPI || stat?.name == PPI_TYPES.BLOCKING_PPI) ? 'snaps' : '%'}
                      </div>
                    </div>
                  : ''
                }
                {
                  stat.versatilityIndex && 
                    <div className={classes.ppiSummaryCard}>
                      <div className={classes.ppiSummaryTitle}>Versatility</div>
                      <div
                        className={clsx(
                        classes.ppiSummaryValue,
                        classes.ppiSummaryValueChip,
                        (getVersatilityType(stat.versatilityIndex) ?? 'Medium') === 'High' && classes.ppiSummaryValueChipHigh,
                        (getVersatilityType(stat.versatilityIndex) ?? 'Medium') === 'Medium' && classes.ppiSummaryValueChipMedium,
                        (getVersatilityType(stat.versatilityIndex) ?? 'Medium') === 'Low' && classes.ppiSummaryValueChipLow,
                      )}>
                        {getVersatilityType(stat.versatilityIndex)}
                      </div>
                      {
                        stat.name === PPI_TYPES.DEFENSIVE_PPI && (
                          <div className={classes.ppiSummaryInfoCenter}>
                            <Tooltip title={DEFENSIVE_PPI_TOOLTIPS.VERSATILITY} placement="top">
                              <InfoIcon className={classes.infoIcon}/>
                            </Tooltip>
                          </div>
                        )
                      }
                    </div>
                }
              </div>

              { stat?.name == PPI_TYPES.DEFENSIVE_PPI && renderDefensePPITable(stat)}
              
              {(stat?.playerFactors?.length || stat?.teamFactors?.length || stat?.criticalFactors?.length) && (
                <div className={classes.ppiTextSections}>
                  {
                    stat?.playerFactors?.length ? (
                      <div className={classes.ppiTextSection}>
                        <div className={classes.ppiTextTitle}>Player Factors:</div>
                        {(stat.playerFactors || []).map((line: string, idx: number) => (
                          <div key={idx} className={classes.ppiBullet}>{line}</div>
                        ))}
                      </div>
                    ) : ''
                  }

                  {
                    stat?.teamFactors?.length ? (
                      <div className={classes.ppiTextSection}>
                        <div className={classes.ppiTextTitle}>Team Factors:</div>
                        {(stat.teamFactors || []).map((line: string, idx: number) => (
                          <div key={idx} className={classes.ppiBullet}>{line}</div>
                        ))}
                      </div>
                    ) : ''
                  }

                  {
                    stat?.criticalFactors?.length ? (
                      <div className={classes.ppiTextSection}>
                        <div className={classes.ppiTextTitle}>Critical Factors:</div>
                        {(stat.criticalFactors || []).map((line: string, idx: number) => (
                          <div key={idx} className={classes.ppiBullet}>{line}</div>
                        ))}
                      </div>
                    ) : ''
                  }
                </div>
              )}
            </div>
        </>
      </Accordion>
    )
  }

  function tableRowData(stat: UpdatedPlayerPPIStatsType): any[] {
    const toOneDecimalOrBlank = (value?: number | null) => {
      const num = Number(value);
      return num ? num.toFixed(1) : '';
    };

    const toPlaysOrBlank = (pct?: number | null, plays?: number | null) => {
      const pctNum = Number(pct);
      const playsNum = Number(plays);
      const total = (pctNum * playsNum) / 100;
      return Number.isFinite(total) && total !== 0 ? Math.round(total) : '';
    };

    const defensePPIRowArray: any = [];
    defensePPIRowArray.push({
      position: 'Safety',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallSafetyPpi),
      plays: toPlaysOrBlank(stat.pctPlaySafety, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseSafetyPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageSafetyPpi),
      passRush: toOneDecimalOrBlank(stat.passRushSafetyPpi),
    });
    defensePPIRowArray.push({
      position: 'Corner',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallCornerPpi),
      plays: toPlaysOrBlank(stat.pctPlayCorner, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseCornerPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageCornerPpi),
      passRush: toOneDecimalOrBlank(stat.passRushCornerPpi),
    });
    defensePPIRowArray.push({
      position: 'OLB',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallOlbPpi),
      plays: toPlaysOrBlank(stat.pctPlayOlb, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseOlbPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageOlbPpi),
      passRush: toOneDecimalOrBlank(stat.passRushOlbPpi),
    });
    defensePPIRowArray.push({
      position: 'MLB',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallMlbPpi),
      plays: toPlaysOrBlank(stat.pctPlayMlb, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseMlbPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageMlbPpi),
      passRush: toOneDecimalOrBlank(stat.passRushMlbPpi),
    });
    defensePPIRowArray.push({
      position: 'DE/Edge',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallOlbEdgePpi),
      plays: toPlaysOrBlank(stat.pctPlayOlbEdge, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseOlbEdgePpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageOlbEdgePpi),
      passRush: toOneDecimalOrBlank(stat.passRushOlbEdgePpi),
    });
    defensePPIRowArray.push({
      position: 'IL',
      overallPPI: toOneDecimalOrBlank(stat.defenseOverallIlPpi),
      plays: toPlaysOrBlank(stat.pctPlayIl, stat.plays),
      runDefense: toOneDecimalOrBlank(stat.runDefenseIlPpi),
      passCoverage: toOneDecimalOrBlank(stat.passCoverageIlPpi),
      passRush: toOneDecimalOrBlank(stat.passRushIlPpi),
    });
    return defensePPIRowArray;
  }

  function renderDefensePPITable(stat: UpdatedPlayerPPIStatsType) {
    return (
      <div className={classes.ppiTableContainer}>
        <table className={classes.ppiTable}>
          <thead>
            <tr>
              <th className={classes.ppiTableHeadCell}>Position Group</th>
              <th className={classes.ppiTableHeadCell}>Overall PPI</th>
              <th className={classes.ppiTableHeadCell}>Plays</th>
              <th className={classes.ppiTableHeadCell}>Run Defense</th>
              <th className={clsx(classes.ppiTableHeadCell)}>Pass Coverage</th>
              <th className={classes.ppiTableHeadCell}>Pass Rush</th>
            </tr>
          </thead>
          <tbody>
            {/* Using single stat as mock Safety row */}
            {
              tableRowData(stat).map((row: any, idx: number) => (
                <tr key={idx}>
                  <td className={classes.ppiTableCell}>{row.position}</td>
                  <td className={classes.ppiTableCell}>{row.overallPPI}</td>
                  <td className={classes.ppiTableCell}>{row.plays}</td>
                  <td className={classes.ppiTableCell}>{row.runDefense}</td>
                  <td className={clsx(classes.ppiTableCell, 
                    row.passCoverage > 3.5 && classes.ppiTableHighlightCell
                    )}>{row.passCoverage}</td>
                  <td className={classes.ppiTableCell}>{row.passRush}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <>
        <div className={classes.yearsContainer}>
          {yearScores.map(({ year, score }) => (
            <button
              key={year}
              type="button"
              className={clsx(
                classes.yearButton,
                selectedYear === year && classes.yearButtonSelected,
              )}
              onClick={() => handleYearClick(year)}
            >
              <span className={classes.yearLabel}>{year}</span>
              <span className={classes.scorePill}>{score}</span>
            </button>
          ))}
        </div>
        <div>
        {selectedYearPPIData.map((stat: UpdatedPlayerPPIStatsType, index: number) => (
          <div key={`${selectedYearData?.year}-${stat.name}`}>
                {renderPPIStats(index, stat.name, stat)}
            </div>
        ))}
        </div>
    </>
  );
}